import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import DynamicSectionsRenderer from '../components/DynamicSectionsRenderer';
import { getProducts, getCategories } from '../api/client';
import { socket } from '../api/socket';
import { isRTL } from '../utils/translations';

const sortCategories = (items = []) => {
    return [...items].sort((a, b) => {
        const aOrder = Number(a?.sortOrder ?? 9999);
        const bOrder = Number(b?.sortOrder ?? 9999);
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
};

const getIconEmoji = (iconName) => {
    switch(iconName) {
        case 'Flame': return '🔥';
        case 'Zap': return '⚡';
        case 'Sparkles': return '✨';
        case 'Gift': return '🎁';
        case 'Percent': return '🏷️';
        case 'Star': return '⭐';
        case 'Heart': return '❤️';
        case 'Clock': return '🕒';
        case 'TrendingUp': return '📈';
        case 'Award': return '🏆';
        default: return '';
    }
};

const CategoryCountdown = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState('');
    useEffect(() => {
        const calculate = () => {
            const diff = new Date(endDate) - new Date();
            if (diff <= 0) {
                setTimeLeft('');
                return;
            }
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const secs = Math.floor((diff % (1000 * 60)) / 1000);
            setTimeLeft(`${hours}h ${mins}m ${secs}s`);
        };
        calculate();
        const timer = setInterval(calculate, 1000);
        return () => clearInterval(timer);
    }, [endDate]);

    if (!timeLeft) return null;
    return (
        <div className="flash-countdown-tag" style={{ fontSize: '10px', color: '#ff4f4f', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255, 79, 79, 0.1)', padding: '2px 6px', borderRadius: '4px', marginTop: '4px' }}>
            <span>⏱️</span> <span>{timeLeft}</span>
        </div>
    );
};

const CategoryPage = ({ onAddToCart, storeSettings }) => {
    const { name } = useParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [fallbackProducts, setFallbackProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(name || 'all');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [initialRequestResolved, setInitialRequestResolved] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const requestCounterRef = useRef(0);

    const toggleExpand = (catId) => {
        setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const recommendationWindow = Math.min(
        6,
        Math.max(1, Number(storeSettings?.recommendationNearbyCategoryWindow) || 2)
    );

    useEffect(() => {
        window.scrollTo(0, 0);
        setActiveCategory(name || 'all');
        setPage(0);
        setFallbackProducts([]);
        setErrorMessage('');
        setInitialRequestResolved(false);
        setLoading(true);
        fetchInitialData(name || 'all');
    }, [name]);

    useEffect(() => {
        const handleNewProduct = (product) => {
            const newProduct = {
                id: product.productId || product.id,
                name: product.name,
                price: product.price,
                compareAtPrice: product.compareAtPrice,
                image: product.imagePath || product.image,
                categoryName: product.category || product.categoryName,
                categoryId: product.categoryId || product.category?.id,
                isNewRealtime: true
            };

            setProducts(prev => {
                if (prev.some(p => String(p.id) === String(newProduct.id))) return prev;

                let shouldAdd = false;
                if (activeCategory === 'all') {
                    shouldAdd = true;
                } else {
                    const currentCategoryName = activeCategory.toLowerCase();
                    const productCategoryName = String(newProduct.categoryName || '').toLowerCase();
                    
                    if (productCategoryName === currentCategoryName) {
                        shouldAdd = true;
                    } else {
                        const targetCat = categories.find(cat => cat.name.toLowerCase() === currentCategoryName);
                        if (targetCat && String(newProduct.categoryId) === String(targetCat.id)) {
                            shouldAdd = true;
                        }
                    }
                }

                if (shouldAdd) {
                    return [newProduct, ...prev];
                }
                return prev;
            });
        };

        const handleProductUpdated = (product) => {
            const productId = product.productId || product.id;
            setProducts(prev => {
                return prev.map(p => {
                    if (String(p.id) === String(productId)) {
                        let belongs = true;
                        if (activeCategory !== 'all') {
                            const currentCategoryName = activeCategory.toLowerCase();
                            const productCategoryName = String(product.category || product.categoryName || '').toLowerCase();
                            
                            let matchesName = productCategoryName === currentCategoryName;
                            let matchesId = false;
                            
                            const targetCat = categories.find(cat => cat.name.toLowerCase() === currentCategoryName);
                            if (targetCat) {
                                const incomingCatId = product.categoryId || product.category?.id;
                                if (incomingCatId && String(incomingCatId) === String(targetCat.id)) {
                                    matchesId = true;
                                }
                            }
                            
                            if (productCategoryName && !matchesName && !matchesId) {
                                belongs = false;
                            }
                        }
                        
                        if (!belongs) {
                            return null;
                        }

                        return {
                            ...p,
                            ...product,
                            id: productId,
                            name: product.name || p.name,
                            price: product.price !== undefined ? product.price : p.price,
                            compareAtPrice: product.compareAtPrice !== undefined ? product.compareAtPrice : p.compareAtPrice,
                            image: product.imagePath || product.image || p.image,
                            categoryName: product.category || product.categoryName || p.categoryName,
                            categoryId: product.categoryId || product.category?.id || p.categoryId
                        };
                    }
                    return p;
                }).filter(Boolean);
            });

            setFallbackProducts(prev => {
                return prev.map(p => {
                    if (String(p.id) === String(productId)) {
                        return {
                            ...p,
                            ...product,
                            id: productId,
                            name: product.name || p.name,
                            price: product.price !== undefined ? product.price : p.price,
                            compareAtPrice: product.compareAtPrice !== undefined ? product.compareAtPrice : p.compareAtPrice,
                            image: product.imagePath || product.image || p.image,
                            categoryName: product.category || product.categoryName || p.categoryName,
                            categoryId: product.categoryId || product.category?.id || p.categoryId
                        };
                    }
                    return p;
                });
            });
        };

        const handleProductDeleted = (product) => {
            const targetId = product.productId || product.id;
            setProducts(prev => prev.filter(p => String(p.id) !== String(targetId)));
            setFallbackProducts(prev => prev.filter(p => String(p.id) !== String(targetId)));
        };

        const handleNewCategory = (cat) => {
            setCategories(prev => {
                if (prev.some(c => c.id === cat.categoryId)) return prev;
                return [...prev, {
                    id: cat.categoryId,
                    name: cat.name,
                    slug: cat.slug,
                    imageUrl: cat.imageUrl,
                    showOnHome: cat.showOnHome,
                    sortOrder: cat.sortOrder,
                    isNewRealtime: true
                }];
            });
        };

        const handleCategoryUpdated = (cat) => {
            setCategories(prev => {
                return prev.map(c => c.id === cat.categoryId ? {
                    ...c,
                    name: cat.name,
                    slug: cat.slug,
                    imageUrl: cat.imageUrl,
                    showOnHome: cat.showOnHome,
                    sortOrder: cat.sortOrder
                } : c);
            });
        };

        const handleCategoryDeleted = (cat) => {
            setCategories(prev => prev.filter(c => c.id !== cat.categoryId));
        };

        socket.on('new-product', handleNewProduct);
        socket.on('product-updated', handleProductUpdated);
        socket.on('product-deleted', handleProductDeleted);
        socket.on('new-category', handleNewCategory);
        socket.on('category-updated', handleCategoryUpdated);
        socket.on('category-deleted', handleCategoryDeleted);

        return () => {
            socket.off('new-product', handleNewProduct);
            socket.off('product-updated', handleProductUpdated);
            socket.off('product-deleted', handleProductDeleted);
            socket.off('new-category', handleNewCategory);
            socket.off('category-updated', handleCategoryUpdated);
            socket.off('category-deleted', handleCategoryDeleted);
        };
    }, [activeCategory, categories]);

    useEffect(() => {
        const handleSync = () => {
            fetchInitialData(activeCategory);
        };
        window.addEventListener('sync-storefront-data', handleSync);
        return () => window.removeEventListener('sync-storefront-data', handleSync);
    }, [activeCategory]);

    const fetchFallbackFromNearbyCategories = async (targetCategory, allCategories) => {
        const sorted = sortCategories(allCategories);
        const currentIndex = sorted.findIndex(cat => Number(cat.id) === Number(targetCategory.id));
        if (currentIndex < 0) return [];

        const candidates = [];
        for (let step = 1; step <= recommendationWindow; step += 1) {
            const left = sorted[currentIndex - step];
            const right = sorted[currentIndex + step];
            if (left) candidates.push(left);
            if (right) candidates.push(right);
        }

        const promises = candidates
            .filter(candidate => candidate && candidate.id)
            .map(candidate => getProducts(0, 12, '', candidate.id));

        const responses = await Promise.allSettled(promises);

        const results = [];
        const seen = new Set();
        for (const response of responses) {
            if (response.status === 'fulfilled') {
                const list = Array.isArray(response.value?.data) ? response.value.data : [];
                list.forEach(item => {
                    const id = Number(item?.id);
                    if (!Number.isInteger(id) || seen.has(id)) return;
                    seen.add(id);
                    results.push(item);
                });
            }
        }

        return results.slice(0, 20);
    };

    // Filter categories to show as horizontal list on mobile
    const horizontalCategories = useMemo(() => {
        if (categories.length === 0) return [];
        if (activeCategory === 'all') {
            return categories.filter(c => !c.parentId);
        }
        const activeCatObj = categories.find(c => c.name.toLowerCase() === activeCategory.toLowerCase());
        if (activeCatObj) {
            const children = categories.filter(c => c.parentId === activeCatObj.id);
            return children.length > 0 ? children : categories.filter(c => !c.parentId);
        }
        return categories.filter(c => !c.parentId);
    }, [categories, activeCategory]);

    const fetchInitialData = async (catName) => {
        const requestId = ++requestCounterRef.current;
        setLoading(true);
        setErrorMessage('');
        try {
            const rawCat = String(catName || 'all').trim();
            const isAll = !rawCat || rawCat.toLowerCase() === 'all' || rawCat.toLowerCase() === 'all collections' || rawCat.toLowerCase() === 'all products';
            
            const [cRaw, pRaw] = await Promise.all([
                getCategories(),
                isAll ? getProducts(0, 30, '', '') : Promise.resolve(null)
            ]);

            const c = sortCategories(cRaw);
            if (requestId !== requestCounterRef.current) return;
            setCategories(c);

            if (isAll) {
                const primaryList = Array.isArray(pRaw?.data) ? pRaw.data : [];
                setProducts(primaryList);
                setTotalPages(Number.isFinite(Number(pRaw?.totalPages)) ? Number(pRaw.totalPages) : 1);
                setPage(0);
                setFallbackProducts([]);
            } else {
                let catId = '';
                let targetCat = c.find(cat => String(cat.name || '').toLowerCase() === rawCat.toLowerCase() || String(cat.slug || '').toLowerCase() === rawCat.toLowerCase());
                if (targetCat) catId = targetCat.id;

                const p = await getProducts(0, 30, '', catId);
                if (requestId !== requestCounterRef.current) return;

                const primaryList = Array.isArray(p?.data) ? p.data : [];
                setProducts(primaryList);
                setTotalPages(Number.isFinite(Number(p?.totalPages)) ? Number(p.totalPages) : 1);
                setPage(0);

                if (primaryList.length === 0) {
                    if (targetCat) {
                        const nearby = await fetchFallbackFromNearbyCategories(targetCat, c);
                        if (requestId !== requestCounterRef.current) return;
                        setFallbackProducts(nearby);
                    } else {
                        // Fallback to all products if category not recognized
                        const fallbackAll = await getProducts(0, 30, '', '');
                        if (requestId !== requestCounterRef.current) return;
                        setProducts(Array.isArray(fallbackAll?.data) ? fallbackAll.data : []);
                    }
                } else {
                    setFallbackProducts([]);
                }
            }
        } catch (error) {
            if (requestId !== requestCounterRef.current) return;
            console.error('Failed to fetch category products:', error);
            setProducts([]);
            setFallbackProducts([]);
            setTotalPages(1);
            setErrorMessage('');
        } finally {
            if (requestId !== requestCounterRef.current) return;
            setLoading(false);
            setInitialRequestResolved(true);
        }
    };

    const loadMore = async () => {
        if (loadingMore || page >= totalPages - 1) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            let catId = '';
            if (activeCategory !== 'all') {
                const targetCat = categories.find(cat => cat.name.toLowerCase() === activeCategory.toLowerCase());
                if (targetCat) catId = targetCat.id;
            }
            const p = await getProducts(nextPage, 20, '', catId);
            const nextList = Array.isArray(p?.data) ? p.data : [];
            setProducts(prev => [...prev, ...nextList]);
            setPage(nextPage);
            setErrorMessage('');
        } catch (error) {
            console.error('Failed to load more products:', error);
            setErrorMessage('Unable to load products');
        } finally {
            setLoadingMore(false);
        }
    };

    const afterProductSections = useMemo(() => {
        const rawList = storeSettings?.homepageSections || [];
        return rawList.filter(section => {
            if (!section || section.enabled === false) return false;
            const targetPage = String(section.targetPage || 'home-page');
            const pagePosition = String(section.pagePosition || 'bottom');
            return (targetPage === 'all-pages' || targetPage === 'category-page') && pagePosition === 'after-product-x';
        }).sort((a, b) => (Number(a.afterProductIndex) || 4) - (Number(b.afterProductIndex) || 4));
    }, [storeSettings?.homepageSections]);

    const renderProductsWithSections = () => {
        if (filtered.length === 0) return null;
        if (afterProductSections.length === 0) {
            return (
                <div className="product-grid">
                    {filtered.map(p => (
                        <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
                    ))}
                </div>
            );
        }

        const elements = [];
        let lastIndex = 0;

        afterProductSections.forEach((section, idx) => {
            const targetIndex = Math.max(1, Number(section.afterProductIndex) || 4);
            if (targetIndex > lastIndex && lastIndex < filtered.length) {
                const chunk = filtered.slice(lastIndex, targetIndex);
                if (chunk.length > 0) {
                    elements.push(
                        <div className="product-grid" key={`grid-chunk-${lastIndex}-${targetIndex}`}>
                            {chunk.map(p => (
                                <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
                            ))}
                        </div>
                    );
                }
                lastIndex = targetIndex;
            }
            elements.push(
                <DynamicSectionsRenderer
                    key={`injected-section-${section.id || idx}`}
                    page="category-page"
                    position="after-product-x"
                    subSections={[section]}
                    allProducts={products}
                    allCategories={categories}
                    onAddToCart={onAddToCart}
                    storeSettings={storeSettings}
                />
            );
        });

        if (lastIndex < filtered.length) {
            elements.push(
                <div className="product-grid" key={`grid-chunk-end-${lastIndex}`}>
                    {filtered.slice(lastIndex).map(p => (
                        <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
                    ))}
                </div>
            );
        }

        return elements;
    };

    const renderCategoryTreeItem = (cat, depth = 0) => {
        const hasChildren = cat.children && cat.children.length > 0;
        const isExpanded = !!expandedCategories[cat.id];
        const isActive = activeCategory.toLowerCase() === cat.name.toLowerCase();
        
        return (
            <div key={cat.id} style={{ marginLeft: `${depth * 8}px` }}>
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: isActive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        borderInlineStart: isActive ? '3px solid #e02424' : '3px solid transparent',
                        transition: 'all 0.3s ease',
                        marginBottom: '4px'
                    }}
                    onClick={() => {
                        if (activeCategory.toLowerCase() !== cat.name.toLowerCase()) {
                            setActiveCategory(cat.name);
                            setPage(0);
                            setFallbackProducts([]);
                            setErrorMessage('');
                            setInitialRequestResolved(false);
                            setLoading(true);
                            fetchInitialData(cat.name);
                        }
                        if (hasChildren) {
                            toggleExpand(cat.id);
                        }
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        {cat.iconName && <span style={{ fontSize: '1.1em', flexShrink: 0 }}>{getIconEmoji(cat.iconName)}</span>}
                        <span style={{ 
                            fontWeight: isActive ? '700' : '400',
                            color: isActive ? '#111827' : 'var(--text-primary)',
                            fontSize: depth === 0 ? '14px' : '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {cat.name} {cat.isTrending && '🔥'}
                        </span>
                        {cat.badgeText && (
                            <span style={{
                                fontSize: '9px',
                                background: 'rgba(255, 79, 79, 0.1)',
                                color: '#ff4f4f',
                                border: '1px solid rgba(255, 79, 79, 0.3)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                flexShrink: 0
                            }}>{cat.badgeText}</span>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {cat.discountBadge && (
                            <span style={{
                                fontSize: '9px',
                                background: 'rgba(254, 44, 85, 0.08)',
                                color: 'var(--lux-accent, #FE2C55)',
                                border: '1px solid rgba(254, 44, 85, 0.25)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontWeight: 'bold'
                            }}>{cat.discountBadge}</span>
                        )}
                        {hasChildren && (
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                {isExpanded ? '▼' : '▶'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Countdown for flash sale if active */}
                {cat.flashSaleEndDate && (
                    <div style={{ paddingLeft: '16px', marginBottom: '8px' }}>
                        <CategoryCountdown endDate={cat.flashSaleEndDate} />
                    </div>
                )}

                {hasChildren && isExpanded && (
                    <div style={{ borderLeft: '1px dashed rgba(255,255,255,0.1)', marginLeft: '18px', paddingLeft: '4px' }}>
                        {cat.children.map(child => renderCategoryTreeItem(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const filtered = products; // Backend handles category filtering
    const showLoadingState = loading || !initialRequestResolved;

    return (
        <div className="page-category">
            <div className="container" style={{ paddingTop: isMobile ? '12px' : '30px', maxWidth: '1400px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/');
                            }
                        }} 
                        className="back-link" 
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            padding: '4px 0', 
                            color: 'inherit', 
                            fontSize: '14px', 
                            fontWeight: '600' 
                        }}
                        aria-label={isRTL() ? 'رجوع' : 'Back'}
                    >
                        {isRTL() ? <ChevronRight size={18} /> : <ChevronLeft size={18} />} 
                        <span>{isRTL() ? 'رجوع' : 'Back'}</span>
                    </button>
                </div>
                <h1 className="section-title" style={{ fontSize: isMobile ? '22px' : '32px', margin: 0 }}>{activeCategory === 'all' ? 'ALL COLLECTIONS' : activeCategory.toUpperCase()}</h1>
                <div className="section-accent" />

                <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row', 
                    gap: isMobile ? '15px' : '30px', 
                    marginTop: isMobile ? '15px' : '30px' 
                }}>
                    {/* Left/Right Sidebar Category Tree Accordion (Desktop Only) */}
                    {!isMobile && (
                        <div className="glass-panel" style={{ 
                            flexShrink: 0, 
                            width: '280px',
                            padding: '24px',
                            height: 'fit-content',
                            position: 'sticky',
                            top: '120px',
                            alignSelf: 'flex-start',
                            zIndex: 10
                        }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.1em', borderBottom: '1px solid var(--lux-line, rgba(255,255,255,0.06))', paddingBottom: '12px' }}>Categories</h3>
                            
                            <div 
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    background: activeCategory === 'all' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                    borderInlineStart: activeCategory === 'all' ? '3px solid #e02424' : '3px solid transparent',
                                    marginBottom: '12px',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => {
                                    setActiveCategory('all');
                                    setPage(0);
                                    setFallbackProducts([]);
                                    setErrorMessage('');
                                    setInitialRequestResolved(false);
                                    setLoading(true);
                                    fetchInitialData('all');
                                }}
                            >
                                <span style={{ fontWeight: activeCategory === 'all' ? '700' : '400', color: activeCategory === 'all' ? '#111827' : 'var(--text-primary)', fontSize: '14px' }}>
                                    All Products
                                </span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {categories
                                    .filter(cat => !cat.parentId)
                                    .map(cat => renderCategoryTreeItem(cat, 0))
                                }
                            </div>
                        </div>
                    )}

                    {/* Right Product Grid */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Slot: top */}
                        <DynamicSectionsRenderer
                            page="category-page"
                            position="top"
                            allProducts={products}
                            allCategories={categories}
                            onAddToCart={onAddToCart}
                            storeSettings={storeSettings}
                        />

                        {/* Mobile Horizontal Scrollable Categories */}
                        {isMobile && horizontalCategories.length > 0 && (
                            <div className="mobile-horizontal-categories" style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '4px 0 16px', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '16px' }}>
                                {horizontalCategories.map(cat => {
                                    const isActive = activeCategory.toLowerCase() === cat.name.toLowerCase();
                                    return (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            onClick={() => {
                                                setActiveCategory(cat.name);
                                                setPage(0);
                                                setFallbackProducts([]);
                                                setErrorMessage('');
                                                setInitialRequestResolved(false);
                                                setLoading(true);
                                                fetchInitialData(cat.name);
                                            }}
                                            style={{
                                                flexShrink: 0,
                                                background: isActive ? '#000000' : 'rgba(0, 0, 0, 0.04)',
                                                border: isActive ? '1px solid #000000' : '1px solid rgba(0, 0, 0, 0.08)',
                                                color: isActive ? '#ffffff' : 'var(--text)',
                                                borderRadius: '20px',
                                                padding: '6px 14px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {getIconEmoji(cat.icon) || '📁'} {isRTL() ? cat.nameAr || cat.name : cat.nameEn || cat.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {showLoadingState ? (
                            <div className="product-grid" aria-label="products-loading">
                                {Array.from({ length: 8 }).map((_, idx) => (
                                    <div className="product-card glass-panel" key={`product-skeleton-${idx}`}>
                                        <div className="product-card-img-wrap">
                                            <div className="skeleton skeleton-img" />
                                        </div>
                                        <div className="product-card-meta">
                                            <div className="skeleton" style={{ width: '35%', height: 10 }} />
                                            <div className="skeleton" style={{ width: '28%', height: 10 }} />
                                        </div>
                                        <div className="skeleton" style={{ width: '70%', height: 16, margin: '10px 0' }} />
                                        <div className="skeleton" style={{ width: '100%', height: 36, borderRadius: 999 }} />
                                    </div>
                                ))}
                            </div>
                        ) : errorMessage ? (
                            <p style={{ color: '#ff8c8c', textAlign: 'center', padding: '60px 0' }}>{errorMessage}</p>
                        ) : filtered.length > 0 ? (
                            <>
                                {renderProductsWithSections()}

                                {page < totalPages - 1 && (
                                    <div style={{ textAlign: 'center', marginTop: '40px' }}>
                                        <button
                                            className="btn-secondary"
                                            onClick={loadMore}
                                            disabled={loadingMore}
                                            style={{ padding: '12px 32px' }}
                                        >
                                            {loadingMore ? 'Loading...' : 'Load More'}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : fallbackProducts.length > 0 ? (
                            <>
                                <p style={{ color: '#8ea0a8', textAlign: 'center', paddingTop: 24 }}>
                                    No products in this category yet. Showing related products from nearby categories.
                                </p>
                                <div className="product-grid">
                                    {fallbackProducts.map(p => (
                                        <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p style={{ color: '#666', textAlign: 'center', padding: '60px 0' }}>No products found in this category.</p>
                        )}

                        {/* Slot: bottom */}
                        <DynamicSectionsRenderer
                            page="category-page"
                            position="bottom"
                            allProducts={products}
                            allCategories={categories}
                            onAddToCart={onAddToCart}
                            storeSettings={storeSettings}
                        />

                        {/* Slot: before-footer */}
                        <DynamicSectionsRenderer
                            page="category-page"
                            position="before-footer"
                            allProducts={products}
                            allCategories={categories}
                            onAddToCart={onAddToCart}
                            storeSettings={storeSettings}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryPage;
